import type { FunctionCallActionNode } from "../../types.js";
import {
  FUNCTION_FLOW_IN_PORT_ID,
  FUNCTION_FLOW_OUT_PORT_ID,
  isFunctionCallBlocked,
} from "../../function-graph.js";
import {
  BehaviorFlow,
  ExecutionContext,
  NodeExecutionResult,
  RuntimeSignal,
} from "../types.js";
import { emptyPatchResult, ensureBindingExpression } from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

/**
 * Invokes a scene function by inlining its body into the parent drain queue.
 * `wait` / `animPlay` delays and the full path to `functionEnd` run in the
 * caller runtime; `functionEnd` resumes the matching `call` flow output port.
 */
export class CallBehaviorNode extends BehaviorNode {
  declare behavior: FunctionCallActionNode;

  constructor(behavior: FunctionCallActionNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const { functionId } = this.behavior;

    if (isFunctionCallBlocked(context.functionCallStack, functionId)) {
      return this.finishBlocked(context);
    }

    const entry = context.functionRuntimeEntries?.get(functionId);
    if (!entry) {
      return this.finishWithoutInvocation(context);
    }

    const triggeredPort =
      context.triggeredInputFlowPort ?? FUNCTION_FLOW_IN_PORT_ID;
    const beginPortId =
      triggeredPort === FUNCTION_FLOW_IN_PORT_ID
        ? FUNCTION_FLOW_OUT_PORT_ID
        : triggeredPort;

    const returnFlowsByPort = this.buildReturnFlowsByPort(context);

    const visiting = new Set<string>();
    const inputs: Record<string, unknown> = {};
    for (const port of this.behavior.ports) {
      if (
        port.direction === "input" &&
        port.id !== FUNCTION_FLOW_IN_PORT_ID &&
        port.valueType !== "flow"
      ) {
        inputs[port.id] = this.getInput(port.id, visiting, context, true);
      }
    }

    const childContext: ExecutionContext = {
      ...context,
      functionCallStack: [
        ...(context.functionCallStack ?? []),
        { functionId, callNodeId: this.behavior.id },
      ],
      functionReturnStack: [
        ...(context.functionReturnStack ?? []),
        { flowsByPort: returnFlowsByPort },
      ],
      functionCallFrameStack: [
        ...(context.functionCallFrameStack ?? []),
        { inputs },
      ],
    };

    const inlinedFlows = entry.runtime.buildFlowsFromFunctionPort(
      childContext,
      beginPortId,
    );
    if (inlinedFlows.length === 0) {
      return this.finishWithoutInvocation(context);
    }

    return {
      patch: emptyPatchResult(),
      nextFlows: inlinedFlows,
      scheduledNodes: [],
      runtimeSignals: [],
    };
  }

  getOutput(
    portId: string,
    visiting: Set<string>,
    context: ExecutionContext,
    binding?: boolean,
  ): unknown {
    if (
      portId === FUNCTION_FLOW_IN_PORT_ID ||
      portId === FUNCTION_FLOW_OUT_PORT_ID
    ) {
      return undefined;
    }

    if (binding !== true) {
      return context.functionCallOutputs?.[portId];
    }

    const { functionId } = this.behavior;
    if (isFunctionCallBlocked(context.functionCallStack, functionId)) {
      return undefined;
    }

    const entry = context.functionRuntimeEntries?.get(functionId);
    if (!entry) {
      return undefined;
    }

    const inputs: Record<string, unknown> = {};
    for (const port of this.behavior.ports) {
      if (
        port.direction === "input" &&
        port.id !== FUNCTION_FLOW_IN_PORT_ID &&
        port.valueType !== "flow"
      ) {
        inputs[port.id] = this.getInput(port.id, visiting, context, true);
      }
    }

    const childContext: ExecutionContext = {
      ...context,
      triggeredInputFlowPort: FUNCTION_FLOW_IN_PORT_ID,
      functionCallStack: [
        ...(context.functionCallStack ?? []),
        { functionId, callNodeId: this.behavior.id },
      ],
      functionCallFrameStack: [
        ...(context.functionCallFrameStack ?? []),
        { inputs },
      ],
    };

    const endNode = [...entry.runtime.nodes.values()].find(
      (node) =>
        node.behavior.type === "control" &&
        node.behavior.controlType === "functionEnd",
    );
    if (!endNode) {
      return undefined;
    }

    const childVisiting = new Set(visiting);
    return ensureBindingExpression(
      endNode.getInput(portId, childVisiting, childContext, true),
    );
  }

  /**
   * Cycle or max depth reached: stop flow without invoking and without `out`.
   */
  private finishBlocked(context: ExecutionContext): NodeExecutionResult {
    const runtimeSignals: RuntimeSignal[] = [];
    if (context.loopStack.length > 0) {
      runtimeSignals.push({ type: "loop_continue", context });
    }
    return {
      patch: emptyPatchResult(),
      nextFlows: [],
      scheduledNodes: [],
      runtimeSignals: [],
    };
  }

  private finishWithoutInvocation(
    context: ExecutionContext,
  ): NodeExecutionResult {
    const nextFlows = this.getNextFlows(context);
    const runtimeSignals: RuntimeSignal[] = [];
    if (nextFlows.length === 0 && context.loopStack.length > 0) {
      runtimeSignals.push({ type: "loop_continue", context });
    }
    return {
      patch: emptyPatchResult(),
      nextFlows,
      scheduledNodes: [],
      runtimeSignals: [],
    };
  }

  private getNextFlows(
    context: ExecutionContext,
    portId: string = FUNCTION_FLOW_OUT_PORT_ID,
  ): BehaviorFlow[] {
    return (this.outgoingEdgesByPort.get(portId) ?? []).map((edge) => ({
      edge,
      context,
    }));
  }

  /**
   * Captures all flow output branches on the `call` node so `functionEnd`
   * can resume the branch matching the end input port that was reached.
   */
  private buildReturnFlowsByPort(
    context: ExecutionContext,
  ): Record<string, BehaviorFlow[]> {
    const flowsByPort: Record<string, BehaviorFlow[]> = {};
    for (const port of this.behavior.ports) {
      if (port.direction === "output" && port.valueType === "flow") {
        flowsByPort[port.id] = this.getNextFlows(context, port.id);
      }
    }
    return flowsByPort;
  }
}
